import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Box, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Chart from "react-apexcharts";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import Label from "src/components/Label";
import {
  getInitials,
  getSubmissionById,
  getSubmissionPersonKey,
  getSubmissionReviewLabel,
  getSubmissionSourceLabel,
  getSubmissionsForTracker,
  SUBMISSION_REVIEW,
  SUBMISSION_SOURCES,
} from "./submissionStorage";
import {
  buildSubmissionPerformance,
  formatDelta,
  formatMetricValue,
  formatPointLabel,
  parseMeasurableNumber,
} from "./submissionPerformance";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const Card = ({ title, description, action, children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: "12px",
      bgcolor: "#fff",
      mb: 2.5,
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      ...sx,
    }}
  >
    {(title || action) && (
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {title ? (
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
              {title}
            </Typography>
          ) : null}
          {description ? (
            <Typography sx={{ mt: 0.35, fontSize: 13, color: MUTED }}>
              {description}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
    )}
    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>{children}</Box>
  </Box>
);

const Fact = ({ label, value }) => (
  <Box
    sx={{
      p: 1.35,
      borderRadius: "8px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#f8fafc",
      height: "100%",
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        color: MUTED,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.5,
        fontSize: 13.5,
        fontWeight: 600,
        color: "#0f172a",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const reviewLabelColor = (status) => {
  if (status === SUBMISSION_REVIEW.OVERDUE) return "error";
  if (status === SUBMISSION_REVIEW.PENDING) return "warning";
  if (status === SUBMISSION_REVIEW.REVIEWED) return "success";
  return "default";
};

const trendMeta = (improved) => {
  if (improved === "up") {
    return {
      color: "#047857",
      bg: "rgba(16,185,129,0.12)",
      label: "Improving",
      Icon: TrendingUpIcon,
    };
  }
  if (improved === "down") {
    return {
      color: "#b91c1c",
      bg: "rgba(239,68,68,0.12)",
      label: "Declining",
      Icon: TrendingDownIcon,
    };
  }
  return {
    color: MUTED,
    bg: "#f1f5f9",
    label: "Stable",
    Icon: TrendingFlatIcon,
  };
};

const overallPerformanceMeta = (metrics = []) => {
  // Verdict from metrics that actually have a trend (2+ readings)
  const trendable = metrics.filter((m) => m.points?.length >= 2);
  const pool = trendable.length ? trendable : metrics;

  if (!pool.length) {
    return {
      label: "No measurable data yet",
      detail: "Add measurable questions to see progress over check-ins.",
      color: MUTED,
      bg: "#f8fafc",
      Icon: TrendingFlatIcon,
    };
  }
  const up = pool.filter((m) => m.improved === "up").length;
  const down = pool.filter((m) => m.improved === "down").length;
  const flat = pool.length - up - down;

  if (up > 0 && down === 0) {
    return {
      label: "On track",
      detail: `${up} metric${up === 1 ? "" : "s"} improving since baseline`,
      color: "#047857",
      bg: "rgba(16,185,129,0.12)",
      Icon: TrendingUpIcon,
      up,
      down,
      flat,
    };
  }
  if (down > 0 && up === 0) {
    return {
      label: "Needs attention",
      detail: `${down} metric${down === 1 ? "" : "s"} moving the wrong way`,
      color: "#b91c1c",
      bg: "rgba(239,68,68,0.12)",
      Icon: TrendingDownIcon,
      up,
      down,
      flat,
    };
  }
  if (up > 0 && down > 0) {
    return {
      label: "Mixed progress",
      detail: `${up} improving · ${down} declining`,
      color: "#b45309",
      bg: "rgba(245,158,11,0.14)",
      Icon: TrendingFlatIcon,
      up,
      down,
      flat,
    };
  }
  return {
    label: "Holding steady",
    detail: "No meaningful change vs baseline yet",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
    up,
    down,
    flat,
  };
};

const MetricChartCard = ({ metric }) => {
  const meta = trendMeta(metric.improved);
  const Icon = meta.Icon;
  const categories = metric.points.map(formatPointLabel);
  const data = metric.points.map((p) => p.value);
  const currentPoint = metric.points.find((p) => p.isCurrent);
  const midLabel = currentPoint ? "THIS CHECK-IN" : "LATEST";
  const midValue = currentPoint
    ? formatMetricValue(currentPoint.value, metric.unit)
    : formatMetricValue(metric.current, metric.unit);

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: "12px",
        border: `1px solid ${BORDER}`,
        bgcolor: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.25,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: "#0f172a" }}>
            {metric.label}
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12, color: MUTED }}>
            {metric.points.length} reading
            {metric.points.length === 1 ? "" : "s"}
            {metric.lowerBetter ? " · lower is better" : " · higher is better"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.4,
            borderRadius: "999px",
            bgcolor: meta.bg,
            color: meta.color,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
            {meta.label}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          mb: 1.25,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 800,
            color: "#0f172a",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          {formatMetricValue(metric.current, metric.unit)}
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: meta.color,
          }}
        >
          {formatDelta(metric.delta, metric.unit, metric.improved)}
          {metric.pctChange != null
            ? ` (${metric.pctChange > 0 ? "+" : ""}${metric.pctChange.toFixed(
                1
              )}%)`
            : ""}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 1,
          mb: 1.25,
          p: 1.1,
          borderRadius: "8px",
          bgcolor: "#f8fafc",
          border: `1px solid ${BORDER}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>
            BASELINE
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {formatMetricValue(metric.baseline, metric.unit)}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>
            {midLabel}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: PRIMARY }}>
            {midValue}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>
            CHANGE
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: meta.color }}>
            {formatDelta(metric.delta, metric.unit, metric.improved)}
          </Typography>
        </Box>
      </Box>

      {metric.points.length >= 2 ? (
        <Box sx={{ mt: "auto" }}>
          <Chart
            options={{
              chart: {
                type: "area",
                toolbar: { show: false },
                zoom: { enabled: false },
                fontFamily: "inherit",
                sparkline: { enabled: false },
              },
              colors: [meta.color === MUTED ? PRIMARY : meta.color],
              dataLabels: { enabled: false },
              stroke: { curve: "smooth", width: 2.5 },
              markers: {
                size: 4,
                hover: { size: 6 },
              },
              fill: {
                type: "gradient",
                gradient: {
                  shadeIntensity: 0.35,
                  opacityFrom: 0.35,
                  opacityTo: 0.05,
                  stops: [0, 90, 100],
                },
              },
              grid: { borderColor: "#f1f5f9", strokeDashArray: 3 },
              xaxis: {
                categories,
                labels: { style: { colors: MUTED, fontSize: "10px" } },
              },
              yaxis: {
                labels: {
                  style: { colors: MUTED, fontSize: "10px" },
                  formatter: (v) =>
                    Number.isFinite(v) ? String(Math.round(v * 10) / 10) : "",
                },
                forceNiceScale: true,
              },
              tooltip: {
                theme: "light",
                y: {
                  formatter: (v) => formatMetricValue(v, metric.unit),
                },
              },
            }}
            series={[{ name: metric.label, data }]}
            type="area"
            height={170}
          />
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.5 }}>
          Only one reading so far — more check-ins will draw the growth chart.
        </Typography>
      )}
    </Box>
  );
};

export default function TrackerSubmissionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [submission, setSubmission] = useState(null);

  useEffect(() => {
    const doc = getSubmissionById(id);
    if (!doc) {
      enqueueSnackbar("Submission not found", { variant: "error" });
      navigate("/tracker-submissions", { replace: true });
      return;
    }
    setSubmission(doc);
  }, [id, enqueueSnackbar, navigate]);

  const performance = useMemo(
    () => (submission ? buildSubmissionPerformance(submission) : null),
    [submission]
  );

  const journeyHistory = useMemo(() => {
    if (!submission?.trackerId) return [];
    const personKey = getSubmissionPersonKey(submission);
    return getSubmissionsForTracker(submission.trackerId)
      .filter((row) => getSubmissionPersonKey(row) === personKey)
      .sort(
        (a, b) =>
          new Date(a.submittedAt || 0) - new Date(b.submittedAt || 0)
      );
  }, [submission]);

  const answerGroups = useMemo(() => {
    if (!submission) return { measurable: [], other: [] };
    const metricLabels = new Set(
      (performance?.metrics || []).map((m) =>
        String(m.label || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, " ")
          .trim()
      )
    );
    const measurable = [];
    const other = [];
    Object.entries(submission.answers || {}).forEach(([key, value]) => {
      const norm = String(key)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
        .replace(/^(starting|current)\s+/, "");
      const isMetric =
        parseMeasurableNumber(value) != null &&
        (metricLabels.has(norm) ||
          /weight|body\s*fat|bmi|energy|sessions|score|height|adherence/i.test(
            key
          ));
      if (isMetric) measurable.push([key, value]);
      else other.push([key, value]);
    });
    return { measurable, other };
  }, [submission, performance]);

  if (!submission) return null;

  const metrics = performance?.metrics || [];
  const displayMetrics = metrics.filter(
    (m) =>
      m.points.length >= 2 ||
      m.points.some((p) => p.isCurrent) ||
      m.improved !== "flat"
  );
  const overall = overallPerformanceMeta(metrics);
  const OverallIcon = overall.Icon;
  const backToList = submission.trackerId
    ? `/tracker-submissions?tracker=${encodeURIComponent(submission.trackerId)}`
    : "/tracker-submissions";

  return (
    <div className="container-fluid tracker-module-page">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            <IconButton
              size="small"
              onClick={() => navigate(backToList)}
              sx={{ p: 0.75 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography
              component="button"
              type="button"
              onClick={() => navigate(backToList)}
              sx={{
                border: 0,
                background: "none",
                p: 0,
                cursor: "pointer",
                color: MUTED,
                fontSize: 13,
                "&:hover": { color: PRIMARY },
              }}
            >
              Submissions
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: 13 }}>/</Typography>
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: 13,
                fontWeight: 600,
                maxWidth: 280,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {submission.memberName}
            </Typography>
            <Label
              variant="ghost"
              color={
                submission.source === SUBMISSION_SOURCES.PUBLIC
                  ? "warning"
                  : "info"
              }
            >
              {getSubmissionSourceLabel(submission.source)}
            </Label>
            <Label
              variant="ghost"
              color={reviewLabelColor(submission.reviewStatus)}
            >
              {getSubmissionReviewLabel(submission.reviewStatus)}
            </Label>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                fontSize: 15,
                fontWeight: 700,
                bgcolor: PRIMARY_SOFT,
                color: PRIMARY,
              }}
            >
              {getInitials(submission.memberName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 22, md: 26 },
                  color: "#0f172a",
                  lineHeight: 1.2,
                }}
              >
                {submission.memberName}
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 13.5, color: MUTED }}>
                {submission.formName || "Form"}
                {submission.trackerName ? ` · ${submission.trackerName}` : ""}
                {submission.submittedAt
                  ? ` · ${moment(submission.submittedAt).format(
                      "DD MMM YYYY HH:mm"
                    )}`
                  : ""}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {submission.trackerId ? (
            <button
              type="button"
              className="small-contained-outlined-button"
              onClick={() => navigate(`/trackers/${submission.trackerId}`)}
            >
              Open tracker
            </button>
          ) : null}
          <button
            type="button"
            className="small-contained-button"
            onClick={() => navigate(backToList)}
          >
            All submissions
          </button>
        </Box>
      </Box>

      {/* Performance snapshot — first thing coaches see */}
      <Box
        sx={{
          mb: 2.5,
          p: { xs: 2, sm: 2.25 },
          borderRadius: "12px",
          border: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(220px, 1.1fr) repeat(3, minmax(0, 1fr))",
          },
          gap: 1.5,
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pr: { md: 1.5 },
            borderRight: { md: `1px solid ${BORDER}` },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: overall.bg,
              color: overall.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <OverallIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.45,
                color: MUTED,
                textTransform: "uppercase",
              }}
            >
              Performance
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 20,
                color: overall.color,
                lineHeight: 1.2,
              }}
            >
              {overall.label}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 12.5, color: MUTED }}>
              {overall.detail}
            </Typography>
          </Box>
        </Box>

        {[
          {
            label: "Check-ins",
            value: String(performance?.historyCount || journeyHistory.length || 1),
            hint: "for this person",
          },
          {
            label: "Improving",
            value: String(overall.up ?? 0),
            hint: "metrics vs baseline",
            color: "#047857",
          },
          {
            label: "Needs work",
            value: String(overall.down ?? 0),
            hint: "metrics vs baseline",
            color: overall.down ? "#b91c1c" : MUTED,
          },
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{
              px: { md: 1 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                color: MUTED,
                textTransform: "uppercase",
              }}
            >
              {stat.label}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                fontSize: 26,
                fontWeight: 800,
                color: stat.color || "#0f172a",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              {stat.value}
            </Typography>
            <Typography sx={{ mt: 0.2, fontSize: 12, color: MUTED }}>
              {stat.hint}
            </Typography>
          </Box>
        ))}
      </Box>

      {displayMetrics.length > 0 ? (
        <Card
          title="Measurable progress"
          description="Baseline → this check-in, across every reading for this person on the journey."
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: displayMetrics.length === 1 ? "1fr" : "1fr 1fr",
              },
              gap: 1.5,
            }}
          >
            {displayMetrics.map((metric) => (
              <MetricChartCard key={metric.id} metric={metric} />
            ))}
          </Box>
        </Card>
      ) : (
        <Card
          title="Measurable progress"
          description="No measurable answers on this journey yet."
        >
          <Typography sx={{ fontSize: 13.5, color: MUTED }}>
            Mark questions as measurable (weight, scores, sessions) to see
            baseline → current trends here.
          </Typography>
        </Card>
      )}

      <div>
          {answerGroups.measurable.length > 0 ? (
            <Card
              title="Numbers on this check-in"
              description="Measurable answers from this submission only."
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "1fr 1fr 1fr",
                  },
                  gap: 1.25,
                }}
              >
                {answerGroups.measurable.map(([key, value]) => (
                  <Box
                    key={key}
                    sx={{
                      p: 1.5,
                      borderRadius: "10px",
                      border: `1px solid ${BORDER}`,
                      bgcolor: PRIMARY_SOFT,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: MUTED,
                        textTransform: "uppercase",
                        letterSpacing: 0.35,
                      }}
                    >
                      {key}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 20,
                        fontWeight: 800,
                        color: "#0f172a",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {String(value || "—")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          ) : null}

          <Card
            title="Notes & answers"
            description="Everything else submitted on this form."
          >
            {answerGroups.other.length === 0 &&
            answerGroups.measurable.length === 0 ? (
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>
                No answers stored on this submission.
              </Typography>
            ) : answerGroups.other.length === 0 ? (
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>
                Only measurable numbers on this check-in — see above.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                  gap: 1.25,
                }}
              >
                {answerGroups.other.map(([key, value]) => (
                  <Fact key={key} label={key} value={String(value || "—")} />
                ))}
              </Box>
            )}
          </Card>

          {journeyHistory.length > 1 ? (
            <Card
              title="Journey check-ins"
              description="Same person on this tracker — click to compare."
            >
              <Box sx={{ display: "grid", gap: 1 }}>
                {journeyHistory.map((row, index) => {
                  const active = row.id === submission.id;
                  return (
                    <Box
                      key={row.id}
                      component="button"
                      type="button"
                      onClick={() => {
                        if (!active) navigate(`/tracker-submissions/${row.id}`);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        textAlign: "left",
                        width: "100%",
                        border: `1px solid ${active ? PRIMARY : BORDER}`,
                        bgcolor: active ? PRIMARY_SOFT : "#fff",
                        borderRadius: "10px",
                        px: 1.5,
                        py: 1.15,
                        cursor: active ? "default" : "pointer",
                        transition: "border-color 0.15s, background 0.15s",
                        "&:hover": active
                          ? undefined
                          : { borderColor: PRIMARY, bgcolor: "#f8fafc" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "999px",
                          bgcolor: active ? PRIMARY : "#e2e8f0",
                          color: active ? "#fff" : "#334155",
                          fontSize: 12,
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: "#0f172a",
                          }}
                        >
                          {row.formName || "Submission"}
                          {active ? " · viewing" : ""}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: MUTED,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.answerSummary ||
                            (row.submittedAt
                              ? moment(row.submittedAt).format("DD MMM YYYY")
                              : "—")}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: MUTED,
                          flexShrink: 0,
                        }}
                      >
                        {row.submittedAt
                          ? moment(row.submittedAt).format("DD MMM")
                          : ""}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Card>
          ) : null}
      </div>
    </div>
  );
}
